import { loadStripe } from '@stripe/stripe-js';

import { stripePublishableKey } from '@config';
import { webappClient } from '@lib/bugsnag';

import { GENERIC_PAYMENT_FAILURE_MESSAGE, PaymentFailedError } from './paymentErrors';
import type { SubscriptionResponse } from './types';

/**
 * Completes SCA/3D Secure for a subscription payment on the client. Resolves on success; throws
 * PaymentFailedError with the specific reason on decline, failed authentication, or any
 * non-succeeded outcome, so the caller can surface it in-context and offer a retry.
 */
export async function confirmSubscriptionPayment(data: SubscriptionResponse): Promise<void> {
  const { clientSecret, paymentMethod } = data;

  const stripe = await loadStripe(stripePublishableKey);
  if (!stripe) {
    throw new Error('Failed to load Stripe');
  }
  if (!clientSecret) {
    throw new PaymentFailedError('Payment authentication could not be started. Please retry.');
  }

  const result = await stripe.confirmCardPayment(clientSecret, {
    payment_method: paymentMethod,
  });

  if (result.error) {
    throw new PaymentFailedError(result.error.message ?? GENERIC_PAYMENT_FAILURE_MESSAGE);
  }
  if (result.paymentIntent?.status === 'succeeded') {
    return;
  }
  // Any other terminal status (e.g. requires_payment_method) means authentication didn't complete.
  throw new PaymentFailedError(GENERIC_PAYMENT_FAILURE_MESSAGE);
}

/**
 * Runs the 3DS challenge for a retried invoice payment, then settles by re-invoking the retry
 * call: the invoice is paid at that point, so the server no-ops and returns the plan/status of
 * the refreshed subscription (including an applied pending update). Falls back to the pre-payment
 * response if the settle call fails — the payment itself has already succeeded by then.
 */
export async function confirmAndSettleRetry(
  data: SubscriptionResponse,
  retryPayment: () => Promise<{ data: SubscriptionResponse }>,
): Promise<SubscriptionResponse> {
  await confirmSubscriptionPayment(data);
  try {
    return (await retryPayment()).data;
  } catch (error) {
    webappClient.notify(error as Error);
    return data;
  }
}
